import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Search, Layers, AlertTriangle, Filter } from 'lucide-react';

export default function InventoryPage() {
  const [inventory, setInventory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchWarehouses();
    fetchInventory();
  }, [warehouseFilter, showLowStock]);

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses');
    }
  };

  const fetchInventory = async () => {
    try {
      const params = new URLSearchParams();
      if (warehouseFilter && warehouseFilter !== 'all') params.append('warehouse_id', warehouseFilter);
      if (showLowStock) params.append('low_stock', 'true');
      
      const response = await api.get(`/inventory?${params.toString()}`);
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.item_sku?.toLowerCase().includes(query) ||
      item.item_name?.toLowerCase().includes(query) ||
      item.warehouse_name?.toLowerCase().includes(query)
    );
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const totalValue = filteredInventory.reduce((sum, item) => sum + (item.total_value || 0), 0);
  const totalUnits = filteredInventory.reduce((sum, item) => sum + (item.quantity_on_hand || 0), 0);
  const lowStockCount = filteredInventory.filter(item => item.quantity_on_hand <= 0 || item.available_quantity <= 0).length;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">INVENTORY</h1>
          <p className="text-[#0A0A0A]/60 mt-1">View stock levels across all locations</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Value</p>
            <p className="kpi-number text-3xl text-[#0A0A0A]">{formatCurrency(totalValue)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Units</p>
            <p className="kpi-number text-3xl text-[#0A0A0A]">{totalUnits.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Low Stock Items</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{lowStockCount}</p>
              </div>
              {lowStockCount > 0 && (
                <AlertTriangle className="w-8 h-8 text-[#FF3B30]" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]/40" />
              <Input
                placeholder="Search by SKU, item name, or warehouse..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-[#E5E5EA]"
                data-testid="search-inventory-input"
              />
            </div>
            <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
              <SelectTrigger className="w-full sm:w-48 border-[#E5E5EA]" data-testid="warehouse-filter">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map((wh) => (
                  <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showLowStock ? "default" : "outline"}
              onClick={() => setShowLowStock(!showLowStock)}
              className={showLowStock ? "bg-[#FF3B30] hover:bg-[#FF3B30]/90" : ""}
              data-testid="low-stock-filter-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Stock Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Layers className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No inventory found</p>
              <p className="text-sm text-[#0A0A0A]/40 mt-1">Create items and process goods receipts to build inventory</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Item Name</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">On Hand</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Reserved</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Available</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Avg Cost</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Total Value</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInventory.map((item) => {
                  const isLowStock = item.quantity_on_hand <= 0 || item.available_quantity <= 0;
                  return (
                    <TableRow key={item.id} className="border-b border-[#E5E5EA]" data-testid={`inventory-row-${item.item_sku}`}>
                      <TableCell className="font-mono text-sm font-medium">{item.item_sku}</TableCell>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell className="text-[#0A0A0A]/60">{item.warehouse_name}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_on_hand}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_reserved}</TableCell>
                      <TableCell className={`text-right font-mono ${isLowStock ? 'text-[#FF3B30]' : ''}`}>
                        {item.available_quantity}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(item.avg_cost)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(item.total_value)}</TableCell>
                      <TableCell>
                        {isLowStock ? (
                          <Badge className="bg-[#FF3B30]/10 text-[#FF3B30]">Low Stock</Badge>
                        ) : (
                          <Badge className="bg-[#34C759]/10 text-[#34C759]">In Stock</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
